import { motion } from "framer-motion";
import { Users, Eye, TrendingUp, DollarSign, Sparkles, Play, ThumbsUp } from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, BarChart, Bar, LineChart, Line } from "recharts";

const areaData = Array.from({ length: 14 }, (_, i) => ({
  d: i,
  v: 20 + Math.round(Math.sin(i / 2) * 12 + i * 3.2 + Math.random() * 4),
}));
const barData = Array.from({ length: 8 }, (_, i) => ({
  d: i,
  v: 30 + Math.round(Math.random() * 60 + i * 6),
}));
const spark = Array.from({ length: 10 }, (_, i) => ({ d: i, v: 40 + Math.round(Math.sin(i) * 15 + i * 3) }));

/**
 * Original floating dashboard composition — inspired by, but not copied from,
 * modern SaaS hero illustrations. Every element is built from primitives.
 */
export default function HeroDashboard() {
  return (
    <div className="relative w-full aspect-[4/4] sm:aspect-[5/4] max-w-[560px] mx-auto">
      {/* Ambient glow */}
      <div className="absolute inset-0 hero-glow rounded-[40px]" />

      {/* Main analytics card */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="absolute top-[10%] left-[6%] w-[62%] card-flat p-5 float-slow"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="overline">Total Views</div>
            <div className="mt-1 text-2xl font-extrabold tabular-nums">218.4M</div>
          </div>
          <div className="flex items-center gap-1 text-xs font-semibold text-emerald-600 bg-emerald-500/10 px-2 py-1 rounded-full">
            <TrendingUp className="w-3 h-3" /> +24.3%
          </div>
        </div>
        <div className="h-24 mt-3">
          <ResponsiveContainer>
            <AreaChart data={areaData}>
              <defs>
                <linearGradient id="orangeArea" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FF6B00" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#FF6B00" stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="v" stroke="#FF6B00" strokeWidth={2.5} fill="url(#orangeArea)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-2 flex justify-between text-[10px] font-mono text-muted-foreground">
          <span>Mon</span><span>Tue</span><span>Wed</span><span>Thu</span><span>Fri</span><span>Sat</span><span>Sun</span>
        </div>
      </motion.div>

      {/* Revenue card - top right */}
      <motion.div
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, delay: 0.25 }}
        className="absolute top-[4%] right-[2%] w-[38%] card-flat p-4 float-medium"
      >
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center">
            <DollarSign className="w-4 h-4 text-emerald-600" />
          </div>
          <div className="overline">Revenue</div>
        </div>
        <div className="mt-3 text-3xl font-extrabold tabular-nums">$142k</div>
        <div className="mt-1 text-[11px] font-medium text-emerald-600">↑ 18% vs last month</div>
        <div className="h-10 mt-2">
          <ResponsiveContainer>
            <BarChart data={barData}>
              <Bar dataKey="v" fill="#10B981" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </motion.div>

      {/* Subscribers pill */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="absolute top-[46%] right-[6%] card-flat px-4 py-3 flex items-center gap-3 float-fast"
      >
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <Users className="w-5 h-5 text-primary" />
        </div>
        <div>
          <div className="text-xs font-semibold text-muted-foreground">Subscribers</div>
          <div className="text-lg font-extrabold tabular-nums leading-tight">21.4M</div>
        </div>
      </motion.div>

      {/* AI insight card */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.5 }}
        className="absolute bottom-[8%] left-[2%] w-[56%] card-flat p-5 float-medium border border-primary/20"
      >
        <div className="flex items-center gap-2 mb-2">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div className="text-xs font-bold uppercase tracking-wider text-primary">AI Insight</div>
        </div>
        <p className="text-[13px] font-medium leading-relaxed">
          Upload consistency is strong. Try shorter titles under 60 chars to lift CTR by ~12%.
        </p>
        <div className="mt-3 flex items-center gap-2">
          <div className="h-1.5 flex-1 bg-secondary rounded-full overflow-hidden">
            <div className="h-full bg-primary rounded-full" style={{ width: "82%" }} />
          </div>
          <span className="text-xs font-mono font-bold text-primary">82</span>
        </div>
      </motion.div>

      {/* Engagement chip bottom right */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.65 }}
        className="absolute bottom-[14%] right-[4%] card-flat p-4 w-[36%] float-slow"
      >
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-500/15 flex items-center justify-center">
            <ThumbsUp className="w-4 h-4 text-blue-600" />
          </div>
          <div className="overline">Engagement</div>
        </div>
        <div className="mt-2 text-2xl font-extrabold tabular-nums">6.8%</div>
        <div className="h-8 mt-1">
          <ResponsiveContainer>
            <LineChart data={spark}>
              <Line type="monotone" dataKey="v" stroke="#3B82F6" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </motion.div>

      {/* Play badge */}
      <motion.div
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.75, type: "spring" }}
        className="absolute top-[36%] left-[38%] w-16 h-16 rounded-2xl bg-primary flex items-center justify-center shadow-2xl shadow-primary/40 float-fast"
      >
        <Play className="w-7 h-7 text-white fill-white ml-1" />
      </motion.div>

      {/* Views chip top center */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.55 }}
        className="absolute -top-2 left-[42%] card-flat px-3 py-2 flex items-center gap-2 float-medium"
      >
        <Eye className="w-3.5 h-3.5 text-primary" />
        <span className="text-xs font-bold tabular-nums">4.2M views</span>
      </motion.div>
    </div>
  );
}
